<script lang="ts" setup>
import { ZoHeader, ZoFooter } from '@zoll/core-layouts/components';
import {
  computed, onMounted, ref, onUnmounted, watch, defineAsyncComponent,
} from 'vue';

import BetaFeatureToggler from '@/components/BetaFeatureToggler.vue';
import DarkModeToggle from '@/components/DarkModeToggle.vue';
import WorkstationModal from '@/components/WorkstationModal.vue';
import ZoLoading from '@/components/elements/Loading.vue';
import LocaleSwitcher from '@/components/layout/LocaleSwitcher.vue';
import Navbar from '@/components/layout/Navbar.vue';
import { FIXED_VALUES } from '@/constants';
import { container } from '@/container';
import { UserController } from '@/controllers/UserController';
import CONSTANTS from '@/helpers/constants';
import { closeIndexDb } from '@/indexedDb/config';
import { t } from '@/locales';
import { CallTypeManager } from '@/managers/CallTypeManager';
import { ConfigurationManager } from '@/managers/ConfigurationManager';
import { MetaDataManager } from '@/managers/MetaDataManager';
import { NatureManager } from '@/managers/NatureManager';
import { PriorityManager } from '@/managers/PriorityManager';
import { TripManager } from '@/managers/TripManager';
import { UnitManager } from '@/managers/UnitManager';
import { ZoneManager } from '@/managers/ZoneManager';
import { BetaFeatures } from '@/services/BetaFeatures';
import { Connection } from '@/services/Connection';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { Router } from '@/services/router/Router';
import { Toaster } from '@/services/toaster/Toaster';
import { useStore as proQaUseStore } from '@/stores/ProQaStore';

document.documentElement.classList.add(navigator.userAgent.includes('Mac OS X') ? 'os-mac' : 'os-win');

const betaFeatures = await container.getAsync(BetaFeatures);
const environment = await container.getAsync(Environment);
const connection = await container.getAsync(Connection);
const userController = await container.getAsync(UserController);
const metaDataManager = await container.getAsync(MetaDataManager);
const configurationManager = container.get(ConfigurationManager);
const callTypeManager = container.get(CallTypeManager);
const natureManager = container.get(NatureManager);
const priorityManager = container.get(PriorityManager);
const tripManager = container.get(TripManager);
const unitManager = container.get(UnitManager);
const zoneManager = container.get(ZoneManager);

const proQaStore = proQaUseStore();
const router = container.get(Router);

const STARTUP_READINESS_TIMEOUT_MS = 12000;
const STARTUP_READINESS_POLL_MS = 100;

type StartupReadiness = {
  isReady: boolean;
  missing: string[];
};

// Refs.
const appIsLoading = ref(router.currentRoute.value.meta.showLoader);
const showWorkstationModal = ref(false);
const isWorkstationEditMode = ref(false);
const windowControls = ref();
const layoutSettings = ref();
const filter = ref();
const proQaStatus = ref();
const notificationComponent = ref();
const logger = container.get(Logger);

const loadWindowControls = async () => {
  windowControls.value = defineAsyncComponent(
    () => import('@/components/WindowControls.vue'),
  );
};

const loadPortalWindowControls = async () => {
  windowControls.value = defineAsyncComponent(
    () => import('@/components/portal/PortalWindowControls.vue'),
  );
};

const loadProQaStatus = async () => {
  proQaStatus.value = defineAsyncComponent(
    () => import('@/components/ProQaStatus.vue'),
  );
};

const loadNotificationComponent = async () => {
  notificationComponent.value = defineAsyncComponent(
    () => import('@/components/Notification.vue'),
  );
};

const loadLayoutSettings = async () => {
  layoutSettings.value = defineAsyncComponent(
    () => import('@/components/LayoutSettings.vue'),
  );
};

const loadFilterSettings = async () => {
  filter.value = defineAsyncComponent(
    () => import('@/components/Filter.vue'),
  );
};

// Computed.
const cadType = computed(() => {
  if (connection.isHybrid) {
    return 'hybrid';
  }
  if (connection.isStandalone) {
    return 'standalone';
  }
  return 'uninitialized';
});

const envClass = computed(() => ({
  'zd-environment--dev': environment.isDev,
  'zd-environment--test': environment.isTest,
  'zd-environment--prod': environment.isProd,
}));

const layoutMode = computed(() => router.currentRoute.value.meta.layout || 'normal');
const layoutClass = computed(() => [`zo-layout--${layoutMode.value}`, appIsLoading.value ? 'is-loading' : '']);
const isNormalLayout = computed(() => layoutMode.value === 'normal');
const shouldHideFooter = computed(() => !isNormalLayout.value);
const isProQaInstalled = computed(() => configurationManager
  .find(CONSTANTS.CONFIGURATIONS.PROQAENABLED)?.value === FIXED_VALUES.YES.toLowerCase());
const proQaWorkstation = computed(() => proQaStore.getWorkstation());
const errorCode = computed(() => (
  router.currentRoute.value.query.code === CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.UnauthorizedCompany ||
  router.currentRoute.value.query.code === CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.AccessDenied ||
  router.currentRoute.value.query.code === CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.UnauthorizedUser ||
  router.currentRoute.value.query.code === CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.NoCompanyConfig ||
  router.currentRoute.value.query.code === CONSTANTS.UI.ERROR_PAGE_CODES.KNOWN.NotInitialized
));

const onClickWorkstation = () => {
  showWorkstationModal.value = true;
  isWorkstationEditMode.value = !!proQaWorkstation.value;
};

const getStartupReadiness = (): StartupReadiness => {
  const checks: Record<string, boolean> = {
    callTypes: callTypeManager.hasLoaded,
    natures: natureManager.hasLoaded,
    priorities: priorityManager.hasLoaded,
    resources: unitManager.hasLoaded,
    trips: tripManager.hasLoaded,
    zones: connection.isStandalone ? zoneManager.hasLoaded : true,
  };

  const missing = Object.entries(checks)
    .filter(([, isReady]) => !isReady)
    .map(([name]) => name);

  return {
    isReady: missing.length === 0,
    missing,
  };
};

const waitForStartupReadiness = async (): Promise<void> => new Promise((resolve) => {
  const startedAt = Date.now();

  const check = () => {
    const readiness = getStartupReadiness();
    if (readiness.isReady) {
      resolve();
      return;
    }

    if (Date.now() - startedAt >= STARTUP_READINESS_TIMEOUT_MS) {
      logger.captureMessage('Startup readiness timeout', {
        tags: { startupReadinessTimeout: true },
        extra: {
          timeoutMs: STARTUP_READINESS_TIMEOUT_MS,
          missingCount: readiness.missing.length,
          missingKeys: readiness.missing.join(','),
        },
      });
      resolve();
      return;
    }

    window.setTimeout(check, STARTUP_READINESS_POLL_MS);
  };

  check();
});

onMounted(async () => {
  appIsLoading.value = true;
  window.addEventListener('click', router.interceptLinkClick);

  try {
    if (connection.isHybrid) {
      logger.track('SessionType', { type: 'Hybrid' });
    }
    else {
      logger.track('SessionType', { type: 'Standalone' });
    }

    showWorkstationModal.value = isProQaInstalled.value && !connection.isPortal;

    const setupTasks: Promise<void>[] = [
      loadProQaStatus(),
      loadNotificationComponent(),
      loadLayoutSettings(),
      loadFilterSettings(),
    ];

    if (connection.isPortal) {
      setupTasks.push(loadPortalWindowControls());
    }
    else {
      setupTasks.push(loadWindowControls());
    }

    await Promise.all(setupTasks);

    // Hold the loader until core startup datasets are present or timeout occurs.
    await waitForStartupReadiness();
  }
  catch (error) {
    // eslint-disable-next-line no-console
    console.error('App startup initialization failed', error);
  }
  finally {
    // Ensure app never stays stuck behind the loading overlay.
    appIsLoading.value = false;
  }
});

onUnmounted(() => {
  closeIndexDb();
  window.removeEventListener('click', router.interceptLinkClick);
});

watch(appIsLoading, async (newValue) => {
  if (!newValue) {
    const showNewBridgeVersionToast = async () => {
      const toaster = await container.getAsync(Toaster);
      toaster.warning(t('notifications.system.errors.version'), {
        context: CONSTANTS.UI.CONTEXTS.GLOBAL,
      });
    };

    if (await metaDataManager.hasNewBridgeVersion()) {
      showNewBridgeVersionToast();
    }
    else {
      watch(() => metaDataManager.hasNewBridgeVersion(), async (value) => {
        if (await value) {
          showNewBridgeVersionToast();
        }
      });
    }
  }
});
</script>

<template>
  <main
    class="zo-dispatch--main"
    :class="layoutClass"
  >
    <ZoHeader
      product="Zoll® Dispatch"
      :product-home-page="environment.baseUrl"
    />
    <div
      id="navbar"
      class="zd-navbar"
    >
      <Navbar
        v-if="!errorCode"
        id="navbar--primary"
        menu="primary"
      >
        <template
          #after
        >
          <component
            :is="{...windowControls}"
            v-if="!appIsLoading"
          />
        </template>
      </Navbar>

      <Navbar
        v-if="!errorCode"
        id="navbar--secondary"
        menu="secondary"
      >
        <template
          #before
        >
          <div class="flex align-items-center gap-3">
            <div
              v-if="isProQaInstalled && !connection.isPortal"
              class="flex align-items-center"
            >
              <component
                :is="proQaStatus"
                v-if="!appIsLoading"
                @click="onClickWorkstation"
              />
            </div>
            <component
              :is="filter"
              v-if="!appIsLoading && connection.isStandalone"
            />
            <component
              :is="layoutSettings"
              v-if="!appIsLoading"
            />
            <component
              :is="notificationComponent"
              v-if="!appIsLoading && !connection.isPortal && !connection.isHybrid"
            />
          </div>
        </template>
        <template
          #after
        >
          <LocaleSwitcher
            v-if="betaFeatures.get('localeSwitcher')?.enabled"
          />
          <BetaFeatureToggler
            v-if="!environment.isProd && betaFeatures.length"
          />
          <DarkModeToggle />
        </template>
      </Navbar>
    </div>
    <div class="zo-dispatch--workspace">
      <router-view
        v-if="!appIsLoading"
        class="zo-dispatch--workspace--content"
      />
    </div>
    <ZoFooter
      id="footer"
      :hidden="shouldHideFooter"
    />
    <transition
      name="fade"
      appear
    >
      <div
        v-if="appIsLoading"
        class="loading-screen"
        qa-id="app_loading_screen"
      >
        <ZoLoading
          qa-id="app_loading"
          :text="t('general.loadingApp')"
          :subtle-text="false"
          :icon="{ name: 'headset', type: 'fas' }"
        />
      </div>
    </transition>
    <WorkstationModal
      v-if="showWorkstationModal"
      :edit-mode="isWorkstationEditMode"
      :workstation="proQaWorkstation"
      @dismiss="showWorkstationModal = false"
    />
    <div
      v-if="userController.activeUser.roles.isZollAdmin()"
      v-tooltip="'CAD type/environment/build version'"
      class="zd-environment"
      :class="envClass"
    >
      {{ cadType }}/{{ environment.name }}/{{ environment.version }}
    </div>
  </main>
</template>

<style lang="scss" scoped>
.zo-header,
.zd-header {
  z-index: 101;
}

.zd-navbar {
  align-items: center;
  background: #fff;
  box-shadow: 0 -1px 0 0 rgba(0, 0, 0, 0.1) inset;
  color: var(--color-primary);
  display: flex;
  justify-content: space-between;
  position: relative;
  transition: 200ms linear;
  z-index: 2;
}

.zd-main {
  flex: 1;
  overflow-x: auto;
  padding: 20px;
}

.zo-footer {
  font-size: 12px;
}

.zd-environment {
  align-items: center;
  background: #444;
  color: #fff;
  cursor: default;
  display: flex;
  font-weight: 700;
  height: 45px;
  left: 0;
  padding: 10px;
  position: fixed;
  text-shadow: 0 0 2px #000;
  top: 0;
  transform: translateX(calc(-100% + 10px));
  transition: transform 300ms ease-in-out;
  white-space: pre;
  z-index: 1000;

  &:hover {
    transform: translateX(0);
  }

  &--dev {
    background: var(--red-base);
  }

  &--test {
    background: var(--orange-base);
  }

  &--prod {
    background: var(--green-base);
  }
}
</style>

<style lang="scss">
  // fixes issue with header extending
  .zo-header .zo-menu-item::before, .zo-header .zo-menu-item::after {
    height: auto !important;
  }
</style>
